import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Row, Col, Button, message } from 'antd';
import { Input, Select } from 'formik-antd';
import { UploadOutlined, FilePdfOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { Box, Text, FormInput } from 'atoms';
import { getSignedUrl } from 'store/commonSlice';
import { searchBankListV2 } from 'store/vendorSliceV2';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';

const { Option } = Select;

/**
 * Convert boolean/string value to select option value
 */
const getSelectValue = (value) => {
	if (value === true || value === 'Yes') {
		return 'Yes';
	}
	if (value === false || value === 'No') {
		return 'No';
	}
	return undefined;
};

const SectionTitle = styled(Text)`
	display: block;
	font-size: 18px;
	font-weight: 600;
	color: #1c1c1c;
	margin-bottom: 24px;
	margin-top: 32px;

	&:first-child {
		margin-top: 0;
	}
`;

const BrowseButton = styled(Button)`
	background-color: #18405d;
	color: #fff;
	border: none;
	border-radius: 4px;
	padding: 8px 20px;
	height: 40px;
	font-size: 14px;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		background-color: #153e60 !important;
		color: #fff !important;
	}
`;

const FileItem = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 16px;
	background-color: #f0f5ff;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	margin-top: 12px;
	gap: 8px;
`;

const FileLink = styled(Text)`
	color: #1890ff;
	cursor: pointer;
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;

const ErrorText = styled(Text)`
	color: #ff4d4f;
	font-size: 12px;
	margin-top: 4px;
	display: block;
`;

// Helper function to extract filename from S3 key
const extractFilenameFromS3Key = (s3Key) => {
	if (!s3Key) return '';
	const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
	const match = s3Key.match(uuidPattern);
	if (match) {
		return s3Key.substring(match[0].length);
	}
	return s3Key;
};

const UPLOADING_TEXT = 'Uploading...';
const BROWSE_FILE_TEXT = 'Browse File';

export default function BankingInformationStep({ isEditable = true, isDisabled = false }) {
	const dispatch = useDispatch();
	const { setFieldValue, values, errors, touched, setFieldTouched } = useFormikContext();

	const disabled = !isEditable || isDisabled;
	const [uploading, setUploading] = useState({
		bank_document: false,
		vrf_document: false,
		udf: false,
	});

	// Bank name dropdown state
	const [bankOptions, setBankOptions] = useState([]);
	const [loadingBanks, setLoadingBanks] = useState(false);
	const bankSearchTimerRef = useRef(null);

	// File preview modal state
	const [previewModal, setPreviewModal] = useState({
		visible: false,
		fileKey: null,
		fileName: '',
	});

	// File states
	const [bankDocumentFile, setBankDocumentFile] = useState(null);
	const [vrfDocumentFile, setVrfDocumentFile] = useState(null);

	const bankDocumentFileInputRef = useRef(null);
	const vrfDocumentFileInputRef = useRef(null);
	const udfFileInputRef = useRef(null);

	/**
	 * Fetch bank list from API
	 * @param {string} queryStr - Optional search string for filtering
	 */
	const fetchBanks = useCallback(
		async (queryStr = '') => {
			setLoadingBanks(true);
			try {
				const filterBy = { is_active: true };
				if (queryStr) {
					filterBy.query_str = queryStr;
				}
				const response = await dispatch(
					searchBankListV2({
						filter_by: filterBy,
						limit: 50,
					})
				);
				const results = response?.results || [];
				const options = results.map((bank) => ({
					value: bank.bank_name,
					label: bank.bank_name,
				}));
				setBankOptions(options);
			} catch (error) {
				console.error('Failed to fetch bank list:', error);
				setBankOptions([]);
			} finally {
				setLoadingBanks(false);
			}
		},
		[dispatch]
	);

	/**
	 * Debounced bank search handler for typeahead
	 */
	const handleBankSearch = useCallback(
		(searchValue) => {
			if (bankSearchTimerRef.current) {
				clearTimeout(bankSearchTimerRef.current);
			}
			bankSearchTimerRef.current = setTimeout(() => {
				fetchBanks(searchValue);
			}, 300);
		},
		[fetchBanks]
	);

	// Load initial bank list on mount
	useEffect(() => {
		fetchBanks();
		return () => {
			if (bankSearchTimerRef.current) {
				clearTimeout(bankSearchTimerRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Restore file displays from Formik values when component mounts or values change
	useEffect(() => {
		// Restore bank document file if key exists but file state doesn't match
		if (
			values.bank_document_key &&
			(!bankDocumentFile || bankDocumentFile.name !== values.bank_document_key)
		) {
			setBankDocumentFile({
				name: values.bank_document_key,
				size: 0, // Size unknown for existing files
			});
		} else if (!values.bank_document_key && bankDocumentFile) {
			// Clear file if key is removed
			setBankDocumentFile(null);
		}

		// Restore VRF document file if key exists but file state doesn't match
		if (
			values.vrf_document_key &&
			(!vrfDocumentFile || vrfDocumentFile.name !== values.vrf_document_key)
		) {
			setVrfDocumentFile({
				name: values.vrf_document_key,
				size: 0,
			});
		} else if (!values.vrf_document_key && vrfDocumentFile) {
			setVrfDocumentFile(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.bank_document_key, values.vrf_document_key]);

	const handleFileSelect = (fieldName, fileInputRef) => {
		fileInputRef.current?.click();
	};

	const handleFileChange = async (event, fieldName, setFileState) => {
		const file = event.target.files?.[0];
		if (!file) return;

		// Validate file size (max 10MB)
		if (file.size > 10 * 1024 * 1024) {
			message.error('File size must be less than 10MB');
			return;
		}

		// Set uploading state
		const uploadKey = fieldName.replace('_file', '').replace('_document', '');
		setUploading((prev) => ({ ...prev, [uploadKey]: true }));

		try {
			// Step 1: Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vendor',
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			// Step 2: Upload file to S3
			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			// Step 3: Store file key in formik
			// Map field names to their corresponding key fields
			let fileKeyField;
			if (fieldName === 'vrf_document_file') {
				fileKeyField = 'vrf_document_key';
			} else if (fieldName === 'bank_document_file') {
				fileKeyField = 'bank_document_key';
			} else {
				fileKeyField = `${fieldName}_key`;
			}
			setFieldValue(fileKeyField, fileData.filename);
			setFileState(file); // Set the file object in state

			message.success('File uploaded successfully');
		} catch (error) {
			console.error('File upload error:', error);
			message.error('Failed to upload file');
		} finally {
			setUploading((prev) => ({ ...prev, [uploadKey]: false }));
			// Reset file input
			if (event.target) {
				event.target.value = '';
			}
		}
	};

	const handleFileRemove = (fieldName, setFileState) => {
		let fileKeyField;
		if (fieldName === 'vrf_document_file') {
			fileKeyField = 'vrf_document_key';
		} else if (fieldName === 'bank_document_file') {
			fileKeyField = 'bank_document_key';
		} else {
			fileKeyField = `${fieldName}_key`;
		}
		setFieldValue(fileKeyField, null);
		setFileState(null);
		// Mark field as touched to show validation error immediately
		setFieldTouched(fileKeyField, true);
		message.info('File removed');
	};

	const handleViewFile = (fileKey, fileName) => {
		if (!fileKey) {
			message.error('No file to view');
			return;
		}
		setPreviewModal({
			visible: true,
			fileKey,
			fileName: fileName || extractFilenameFromS3Key(fileKey),
		});
	};

	const handleClosePreview = () => {
		setPreviewModal({
			visible: false,
			fileKey: null,
			fileName: '',
		});
	};

	return (
		<>
			{/* Hidden file inputs */}
			<input
				type="file"
				ref={bankDocumentFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) =>
					!disabled && handleFileChange(e, 'bank_document_file', setBankDocumentFile)
				}
			/>
			<input
				type="file"
				ref={vrfDocumentFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) =>
					!disabled && handleFileChange(e, 'vrf_document_file', setVrfDocumentFile)
				}
			/>
			<input
				type="file"
				ref={udfFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) => !disabled && handleFileChange(e, 'udf_file')}
			/>

			<SectionTitle>Banking Details</SectionTitle>

			{/* Account Holder Name & Account Number */}
			<Row gutter={[24, 0]}>
				<Col span={12}>
					<FormInput label="Account Holder Name" required name="account_holder_name">
						<Input
							name="account_holder_name"
							placeholder="Eg : XYZ Logistics"
							disabled={disabled}
						/>
					</FormInput>
				</Col>
				<Col span={12}>
					<FormInput label="Account Number" required name="account_number">
						<Input
							name="account_number"
							placeholder="Eg : 1234567890123"
							disabled={disabled}
							maxLength={18}
							onChange={(e) => {
								// Only allow digits, remove any non-digit characters
								const value = e.target.value.replace(/[^0-9]/g, '');
								setFieldValue('account_number', value);
							}}
							onBlur={(e) => {
								// Trim leading and trailing spaces
								const trimmedValue = e.target.value.trim();
								setFieldValue('account_number', trimmedValue);
							}}
						/>
					</FormInput>
				</Col>
			</Row>

			{/* Bank Name */}
			<Row gutter={[24, 0]}>
				<Col span={12}>
					<FormInput label="Bank Name" required name="bank_name">
						<Select
							name="bank_name"
							placeholder="Search and select bank"
							disabled={disabled}
							showSearch
							loading={loadingBanks}
							onSearch={handleBankSearch}
							filterOption={false}
							notFoundContent={loadingBanks ? 'Loading banks...' : 'No banks found'}
						>
							{bankOptions.map((option) => (
								<Option key={option.value} value={option.value}>
									{option.label}
								</Option>
							))}
						</Select>
					</FormInput>
				</Col>
			</Row>

			{/* Branch Name & IFSC Code */}
			<Row gutter={[24, 0]}>
				<Col span={12}>
					<FormInput label="Branch Name" required name="branch_name">
						<Input name="branch_name" placeholder="Eg : Ripplr" disabled={disabled} />
					</FormInput>
				</Col>
				<Col span={12}>
					<FormInput label="IFSC Code" required name="ifsc_code">
						<Input
							name="ifsc_code"
							placeholder="Eg : HDFC0001234"
							disabled={disabled}
							maxLength={11}
							onChange={(e) => {
								const value = e.target.value.toUpperCase();
								setFieldValue('ifsc_code', value);
							}}
						/>
					</FormInput>
				</Col>
			</Row>

			{/* Bank Document Upload */}
			<Row gutter={[24, 0]}>
				<Col span={24}>
					<FormInput label="Upload Bank Document" required name="bank_document_file_key">
						<Box>
							<BrowseButton
								icon={<UploadOutlined />}
								loading={uploading.bank_document}
								disabled={disabled}
								onClick={() =>
									!disabled &&
									handleFileSelect('bank_document_file', bankDocumentFileInputRef)
								}
							>
								{uploading.bank_document ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
							</BrowseButton>
							{bankDocumentFile && (
								<FileItem>
									<Box display="flex" alignItems="center" gap={8}>
										<FilePdfOutlined
											style={{ color: '#ff4d4f', fontSize: '18px' }}
										/>
										<FileLink fontSize={14}>
											{extractFilenameFromS3Key(bankDocumentFile.name)}
										</FileLink>
									</Box>
									<Box display="flex" alignItems="center" gap={8}>
										<EyeOutlined
											style={{
												cursor: 'pointer',
												color: '#1890ff',
												fontSize: '16px',
											}}
											onClick={() =>
												handleViewFile(
													values.bank_document_key,
													bankDocumentFile.name
												)
											}
										/>
										{!disabled && (
											<CloseOutlined
												style={{
													cursor: 'pointer',
													color: '#ff4d4f',
													fontSize: '14px',
												}}
												onClick={() =>
													handleFileRemove(
														'bank_document_file',
														setBankDocumentFile
													)
												}
											/>
										)}
									</Box>
								</FileItem>
							)}
							{errors.bank_document_key && touched.bank_document_key && (
								<ErrorText>Please upload the bank document</ErrorText>
							)}
						</Box>
					</FormInput>
				</Col>
			</Row>

			<SectionTitle>Other details</SectionTitle>

			{/* TDS Applicable, Vendor Registered MSME, MSME Number */}
			<Row gutter={[24, 0]}>
				<Col span={8}>
					<FormInput label="TDS Applicable" required name="tds_applicable">
						<Select
							name="tds_applicable"
							placeholder="Select"
							disabled={disabled}
							onChange={(value) => {
								const boolValue = value === 'Yes' || value === true;
								setFieldValue('tds_applicable', boolValue);
							}}
							value={getSelectValue(values.tds_applicable)}
						>
							<Option value="Yes">Yes</Option>
							<Option value="No">No</Option>
						</Select>
					</FormInput>
				</Col>
				<Col span={8}>
					<FormInput
						label="Vendor Registered MSME"
						required
						name="vendor_registered_msme"
					>
						<Select
							name="vendor_registered_msme"
							placeholder="Select"
							disabled={disabled}
							onChange={(value) => {
								const boolValue = value === 'Yes' || value === true;
								setFieldValue('vendor_registered_msme', boolValue);
								// Clear MSME number if set to No (VRF document is independent)
								if (!boolValue) {
									setFieldValue('msme_number', '');
								}
							}}
							value={getSelectValue(values.vendor_registered_msme)}
						>
							<Option value="Yes">Yes</Option>
							<Option value="No">No</Option>
						</Select>
					</FormInput>
				</Col>
				<Col span={8}>
					<FormInput
						label="MSME Number"
						required={values.vendor_registered_msme}
						name="msme_number"
					>
						<Input
							name="msme_number"
							placeholder="Eg : MSME123456"
							disabled={disabled || !values.vendor_registered_msme}
						/>
					</FormInput>
				</Col>
			</Row>

			{/* VRF Document Upload */}
			<Row gutter={[24, 0]}>
				<Col span={24}>
					<FormInput label="VRF Document Upload" required name="vrf_document_file_key">
						<Box>
							<BrowseButton
								icon={<UploadOutlined />}
								loading={uploading.vrf_document}
								disabled={disabled}
								onClick={() =>
									!disabled &&
									handleFileSelect('vrf_document_file', vrfDocumentFileInputRef)
								}
							>
								{uploading.vrf_document ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
							</BrowseButton>
							{vrfDocumentFile && (
								<FileItem>
									<Box display="flex" alignItems="center" gap={8}>
										<FilePdfOutlined
											style={{ color: '#ff4d4f', fontSize: '18px' }}
										/>
										<FileLink fontSize={14}>
											{extractFilenameFromS3Key(vrfDocumentFile.name)}
										</FileLink>
									</Box>
									<Box display="flex" alignItems="center" gap={8}>
										<EyeOutlined
											style={{
												cursor: 'pointer',
												color: '#1890ff',
												fontSize: '16px',
											}}
											onClick={() =>
												handleViewFile(
													values.vrf_document_key,
													vrfDocumentFile.name
												)
											}
										/>
										{!disabled && (
											<CloseOutlined
												style={{
													cursor: 'pointer',
													color: '#ff4d4f',
													fontSize: '14px',
												}}
												onClick={() =>
													handleFileRemove(
														'vrf_document_file',
														setVrfDocumentFile
													)
												}
											/>
										)}
									</Box>
								</FileItem>
							)}
							{errors.vrf_document_key && touched.vrf_document_key && (
								<ErrorText>Please upload the VRF document</ErrorText>
							)}
						</Box>
					</FormInput>
				</Col>
			</Row>

			{/* File Preview Modal */}
			<FilePreviewModal
				visible={previewModal.visible}
				fileKey={previewModal.fileKey}
				fileName={previewModal.fileName}
				entity="vendor"
				onClose={handleClosePreview}
			/>
		</>
	);
}

BankingInformationStep.propTypes = {
	isEditable: PropTypes.bool,
	isDisabled: PropTypes.bool,
};
